// Considered: FilterProvenanceGroupMolecule (EAINT-781) — rejected. That
// component groups by ORIGIN WITHIN ONE PROPERTY for the per-property
// popover; this groups by LEVEL ACROSS EVERY PROPERTY for the persistent
// bar's summary. Same chips, orthogonal grouping axis — one component
// parameterised by axis would take two prop sets that never co-occur. See
// spec Reuse Audit (specs/context-filter-lifecycle.md).
//
// Reuses FilterChipsDisplayMolecule UNMODIFIED (`showHeader`/`locked`
// already carry what a (level × layer) group needs) for every clause that
// still resolves. A layer's DEGRADED clauses are rendered directly with
// `ChipAtom.unavailable` instead — degraded status lives on `useLevelFilters
// ().degraded`, which FilterChipsDisplayMolecule has no notion of, so
// folding a degraded clause into that molecule's props would mean inventing
// a prop it was deliberately not given (spec Reuse Audit: "no new prop
// needed").
import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'localization';
import { useAnalytics } from '@center-technologies/analytics';
import { hasAnyFilterLane } from '@center-platform/spheres/filters';
import type { ComposedLevelFilters, DegradedFilterEntry, FilterLayer, FilterProvenanceEntry } from 'types/contextView';
import { groupComposedFiltersByLayer } from '../../../utils/composeLevelFilters';
import { perspectiveUnifiedToState } from '../../../utils/contextFlowSerialization';
import FilterChipsDisplayMolecule from '../FilterChipsDisplay/FilterChipsDisplayMolecule';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import './FilterSummaryGroupMolecule.scss';

/**
 * Contract order, and the same five layers `LevelFilterPopoverOrganism`'s
 * `DISPLAY_LAYER_ORDER` carries. The two surfaces differ in what they can SAVE
 * to, not in what they show: a link's clauses are not editable in that popover,
 * but both places must show them — this bar is where a recipient sees what is
 * actually filtering their board.
 *
 * Omitting `link` here left the bar counting link provenance into its badge
 * (`ContextFilterBarMolecule`) while no surface rendered the clauses — "3 filters"
 * over an empty panel. `link` renders last because it replaces `view`/`user`/`session`
 * wholesale, and always locked (`canRemoveLayer('link') === false`).
 */
const LAYER_ORDER: FilterLayer[] = ['context', 'view', 'user', 'session', 'link'];

const ORIGIN_KEY: Record<FilterLayer, string> = {
  context: 'columnFilters.provenance.context.heading',
  view: 'columnFilters.provenance.view.heading',
  user: 'columnFilters.provenance.user.heading',
  session: 'columnFilters.provenance.session.heading',
  link: 'columnFilters.provenance.link.heading',
};

const REASON_KEY: Record<FilterLayer, string> = {
  context: 'columnFilters.provenance.context.lockedReason',
  view: 'columnFilters.provenance.view.lockedReason',
  user: 'columnFilters.provenance.user.lockedReason',
  session: 'columnFilters.provenance.session.lockedReason',
  link: 'columnFilters.provenance.link.lockedReason',
};

type DegradableLane = Extract<FilterProvenanceEntry['lane'], 'propertyFilters' | 'perspectiveFilters' | 'relationshipFilters'>;

interface LaneSplit<T> {
  /** Still-resolving clauses, for `FilterChipsDisplayMolecule`. */
  normal: T[];
  /** `normal[i]`'s filterId, index-aligned — lets a remove callback (which only gets an array index or a name) resolve back to the id `removeFilterClause` needs. */
  normalIds: string[];
}

function splitLane<T>(
  clauses: T[],
  layer: FilterLayer,
  lane: DegradableLane,
  provenance: FilterProvenanceEntry[],
  degradedIds: Set<string>
): LaneSplit<T> {
  const laneProvenance = provenance.filter((p) => p.layer === layer && p.lane === lane);
  const normal: T[] = [];
  const normalIds: string[] = [];
  clauses.forEach((clause, index) => {
    const filterId = laneProvenance[index]?.filterId;
    if (filterId && degradedIds.has(filterId)) return;
    normal.push(clause);
    normalIds.push(filterId ?? '');
  });
  return { normal, normalIds };
}

function degradedLabel(
  entry: DegradedFilterEntry,
  getPropertyLabel: (key: string, aspect?: string) => string,
  getPerspectiveLabel: (name: string) => string,
  getRelationshipLabel: (name: string) => string
): string {
  switch (entry.lane) {
    case 'propertyFilters':
      return getPropertyLabel(entry.missingTarget);
    case 'perspectiveFilters':
      return getPerspectiveLabel(entry.missingTarget);
    case 'relationshipFilters':
      return getRelationshipLabel(entry.missingTarget);
    default:
      return entry.missingTarget;
  }
}

export interface FilterSummaryGroupMoleculeProps {
  contextSystemName?: string | null;
  /** The `ContextLevel.id` this group summarizes. */
  levelId: string;
  levelLabel: string;
  /** `useLevelFilters().byLevel[levelId]`. */
  composed: ComposedLevelFilters;
  /** The FULL `useLevelFilters().degraded` array — filtered to this level internally, so every group can share one array from the caller. */
  degraded: DegradedFilterEntry[];
  /** `useLevelFilters().canRemoveLayer` — the single predicate deciding whether a layer's chips render a remove control. No `layer === 'view'` branch here; the hook already resolved it. */
  canRemoveLayer: (layer: FilterLayer) => boolean;
  /** `useLevelFilters().removeFilterClause`, pre-bound to nothing — this component supplies every argument per click. */
  onRemove: (levelId: string, layer: FilterLayer, lane: FilterProvenanceEntry['lane'], filterId: string) => void;
  /** Forwarded to `FilterChipsDisplayMolecule` — see its doc for the `aspect` parameter. */
  getPropertyLabel?: (propertyKey: string, aspect?: string) => string;
  getPerspectiveLabel?: (perspectiveSystemName: string) => string;
  getRelationshipLabel?: (relationshipSystemName: string) => string;
  getPropertyValueLabel?: (propertyKey: string, value: string) => string;
  className?: string;
}

const FilterSummaryGroupMolecule: React.FC<FilterSummaryGroupMoleculeProps> = ({
  contextSystemName,
  levelId,
  levelLabel,
  composed,
  degraded,
  canRemoveLayer,
  onRemove,
  getPropertyLabel = (key) => key,
  getPerspectiveLabel = (name) => name,
  getRelationshipLabel = (name) => name,
  getPropertyValueLabel = (_key, value) => value,
  className = '',
}) => {
  const { t } = useTranslation();
  const logAnalyticsEvent = useAnalytics();

  const byLayer = useMemo(() => groupComposedFiltersByLayer(composed), [composed]);

  const degradedByLayer = useMemo(() => {
    const result: Record<FilterLayer, DegradedFilterEntry[]> = { context: [], view: [], user: [], session: [], link: [] };
    degraded.filter((entry) => entry.levelId === levelId).forEach((entry) => result[entry.layer].push(entry));
    return result;
  }, [degraded, levelId]);

  // Fires once per distinct degraded clause, not once per render — a group
  // that re-renders while the same clause is still degraded must not spam
  // the event (mirrors `FilterConflictNoticeMolecule`'s `firedForRef`).
  const firedForRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    Object.values(degradedByLayer)
      .flat()
      .forEach((entry) => {
        if (firedForRef.current.has(entry.filterId)) return;
        firedForRef.current.add(entry.filterId);
        logAnalyticsEvent('context_filter_degraded_shown', undefined, {
          contextSystemName: contextSystemName ?? null,
          layer: entry.layer,
          lane: entry.lane,
        });
      });
  }, [degradedByLayer, contextSystemName, logAnalyticsEvent]);

  const activeLayers = LAYER_ORDER.filter((layer) => hasAnyFilterLane(byLayer[layer]));

  if (activeLayers.length === 0) return null;

  return (
    <div className={`filter-summary-group ${className}`.trim()}>
      <div className="filter-summary-group__level-label">{levelLabel}</div>
      {activeLayers.map((layer) => {
        const layerFilters = byLayer[layer];
        const layerDegraded = degradedByLayer[layer];
        const degradedIds = new Set(layerDegraded.map((entry) => entry.filterId));
        const removable = canRemoveLayer(layer);

        const propertySplit = splitLane(
          layerFilters.propertyFilters ?? [],
          layer,
          'propertyFilters',
          composed.provenance,
          degradedIds
        );
        const perspectiveSplit = splitLane(
          layerFilters.perspectiveFilters ?? [],
          layer,
          'perspectiveFilters',
          composed.provenance,
          degradedIds
        );
        const relationshipSplit = splitLane(
          layerFilters.relationshipFilters ?? [],
          layer,
          'relationshipFilters',
          composed.provenance,
          degradedIds
        );
        // EAINT-793 R-19: `activeLayers` above is gated by `hasAnyFilterLane`,
        // which counts `arcFilters` — so a layer whose only clause on this
        // level is an arc row reached this render and drew an origin label
        // over no chips at all.
        //
        // Follows the `filterGroups` shape, not the `splitLane` one: `splitLane`
        // takes a `DegradableLane`, and `arcFilters` is deliberately not one (an
        // arc row names no catalog property that could go missing), so there is
        // no degraded partition to make here — only ids to resolve for removal.
        const arcProvenance = composed.provenance.filter((p) => p.layer === layer && p.lane === 'arcFilters');
        const groupProvenance = composed.provenance.filter((p) => p.layer === layer && p.lane === 'filterGroups');

        const perspectiveIdByName = new Map<string, string>();
        perspectiveSplit.normal.forEach((filter, index) => {
          perspectiveIdByName.set(filter.perspectiveSystemName, perspectiveSplit.normalIds[index]);
        });

        return (
          <div key={layer} className="filter-summary-group__layer">
            <span className="filter-summary-group__origin">{t(ORIGIN_KEY[layer])}</span>
            <div className="filter-summary-group__chips">
              <FilterChipsDisplayMolecule
                propertyFilters={propertySplit.normal}
                perspectiveFilters={perspectiveSplit.normal.map(perspectiveUnifiedToState)}
                relationshipFilters={relationshipSplit.normal}
                arcFilters={layerFilters.arcFilters}
                filterGroups={layerFilters.filterGroups}
                showHeader={false}
                showClearAll={false}
                locked={!removable}
                lockedReason={t(REASON_KEY[layer])}
                getPropertyLabel={getPropertyLabel}
                getPerspectiveLabel={getPerspectiveLabel}
                getRelationshipLabel={getRelationshipLabel}
                getPropertyValueLabel={getPropertyValueLabel}
                onRemovePropertyFilter={
                  removable ? (index) => onRemove(levelId, layer, 'propertyFilters', propertySplit.normalIds[index]) : undefined
                }
                onRemovePerspectiveFilter={
                  removable
                    ? (perspectiveSystemName) => {
                        const filterId = perspectiveIdByName.get(perspectiveSystemName);
                        if (filterId) onRemove(levelId, layer, 'perspectiveFilters', filterId);
                      }
                    : undefined
                }
                onRemoveRelationshipFilter={
                  removable
                    ? (index) => onRemove(levelId, layer, 'relationshipFilters', relationshipSplit.normalIds[index])
                    : undefined
                }
                onRemoveArcFilter={
                  removable
                    ? (index) => {
                        const filterId = arcProvenance[index]?.filterId;
                        if (filterId) onRemove(levelId, layer, 'arcFilters', filterId);
                      }
                    : undefined
                }
                onRemoveFilterGroup={
                  removable
                    ? (index) => {
                        const filterId = groupProvenance[index]?.filterId;
                        if (filterId) onRemove(levelId, layer, 'filterGroups', filterId);
                      }
                    : undefined
                }
              />
              {layerDegraded.map((entry) => (
                <ChipAtom
                  key={entry.filterId}
                  label={degradedLabel(entry, getPropertyLabel, getPerspectiveLabel, getRelationshipLabel)}
                  unavailable
                  unavailableReason={t('columnFilters.bar.unavailable')}
                  removable={removable}
                  onRemove={
                    removable
                      ? () => {
                          onRemove(levelId, layer, entry.lane, entry.filterId);
                          return false;
                        }
                      : undefined
                  }
                  className="filter-summary-group__degraded-chip"
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FilterSummaryGroupMolecule;
